/**
 * Access Control Service
 * 
 * Provides field-level access control checking and masking utilities.
 * Leverages existing authService for permission checking - does not duplicate permission logic.
 */

import { authService } from './authService'
import { emergencyOverrideService } from './emergencyOverrideService'
import type { ConfidentialityLevel } from '../types/confidentiality'

/**
 * Emergency override request
 */
export interface OverrideRequest {
  id: string
  userId: string
  patientId: string
  fields: string[]
  reason: string
  status: 'pending' | 'approved' | 'rejected' | 'expired'
  requestedAt: string
  approvedBy?: string
  approvedAt?: string
  expiresAt?: string
  rejectedBy?: string
  rejectedAt?: string
  rejectionReason?: string
}

/**
 * Active override for a user/patient combination
 */
export interface ActiveOverride {
  id: string
  userId: string
  patientId: string
  fields: string[]
  reason: string
  expiresAt: string
  approvedBy: string
  approvedAt: string
}

/**
 * Access control service
 * 
 * All access decisions flow through existing permission system (authService.hasPermission)
 */
export const accessControlService = {
  /**
   * Check if user can access a specific field
   * 
   * @param field - Field identifier (e.g., 'medications', 'conditions', 'sensitive-documents')
   * @param patientId - Optional patient ID for patient-specific checks
   * @param confidentialityLevel - Optional confidentiality level of the field
   * @returns true if user can access the field
   */
  canAccessField: (
    field: string,
    _patientId?: string,
    confidentialityLevel?: ConfidentialityLevel
  ): boolean => {
    // Check field-level permission via existing authService
    // Role permissions define what each role can access
    if (authService.hasFieldPermission(field)) {
      return true
    }

    // Check if field requires full access for confidential/highly-confidential levels
    if (confidentialityLevel === 'confidential' || confidentialityLevel === 'highly-confidential') {
      // For confidential/highly-confidential, require explicit full-access permission
      // 'patients:read' alone is not sufficient
      return authService.hasPermission('patients:read:full-access')
    }

    // Note: Active emergency overrides are checked asynchronously in components
    // This synchronous check is for permission-based access only
    return false
  },

  /**
   * Get masked value for a field
   * 
   * @param _field - Field identifier (unused but kept for API consistency)
   * @param value - Original value to mask
   * @param maskType - Type of masking to apply ('full' | 'partial' | 'metadata-only')
   * @returns Masked value or null if field should be hidden
   */
  getMaskedValue: (
    _field: string,
    value: any,
    maskType: 'full' | 'partial' | 'metadata-only' = 'full'
  ): any => {
    if (value === null || value === undefined) {
      return null
    }

    switch (maskType) {
      case 'full':
        // Complete masking - return null or placeholder
        return null
      case 'partial':
        // Partial masking - show some metadata, hide details
        if (Array.isArray(value)) {
          return `[${value.length} items - Restricted]`
        }
        if (typeof value === 'object') {
          return '[Restricted]'
        }
        return '[Restricted]'
      case 'metadata-only':
        // Show metadata only (e.g., count, type) but not actual data
        if (Array.isArray(value)) {
          return { count: value.length, type: 'array', restricted: true }
        }
        if (typeof value === 'object') {
          return { type: 'object', restricted: true }
        }
        return { type: typeof value, restricted: true }
      default:
        return null
    }
  },

  /**
   * Request emergency access override
   * 
   * @param fields - Fields to request access for
   * @param patientId - Patient ID
   * @param reason - Reason for override request
   * @returns Override request
   */
  requestEmergencyAccess: async (
    fields: string[],
    patientId: string,
    reason: string
  ): Promise<OverrideRequest> => {
    // Check if user has override permission
    if (!authService.canOverrideAccess()) {
      throw new Error('User does not have permission to request emergency access')
    }

    return await emergencyOverrideService.request({
      patientId,
      fields,
      reason,
    })
  },

  /**
   * Check if user has active override for field(s)
   * 
   * @param fields - Fields to check
   * @param patientId - Patient ID
   * @returns Active override if exists, null otherwise
   */
  getActiveOverride: async (
    fields: string[],
    patientId: string
  ): Promise<ActiveOverride | null> => {
    try {
      const activeOverrides = await emergencyOverrideService.getActive(patientId, fields)
      // Return the first active override that matches the requested fields
      const matchingOverride = activeOverrides.find((override) =>
        fields.some((field) => override.fields.includes(field))
      )
      return matchingOverride || null
    } catch (error) {
      console.error('Error fetching active override:', error)
      return null
    }
  },

  /**
   * Check if override is active and not expired
   * 
   * @param override - Override to check
   * @returns true if override is active and not expired
   */
  isOverrideActive: (override: ActiveOverride | OverrideRequest | null): boolean => {
    if (!override) return false

    if ('status' in override) {
      if (override.status !== 'approved') {
        return false
      }
      if (override.expiresAt) {
        return new Date(override.expiresAt) > new Date()
      }
      return true
    }

    if (override.expiresAt) {
      return new Date(override.expiresAt) > new Date()
    }

    return false
  },
}
